import { useQuery } from "@apollo/react-hooks";
import { ApolloError, ApolloQueryResult, gql } from "@apollo/client";
import { useAuth } from "src/hooks/useAuth";
import { OrderQueryResult } from "src/types/ctgraphql.d";
import { storeKey, storeConfig } from "src/config/app-config";
import { moneyFragment, addressFragment } from "./useCart";

export interface MyOrdersVars {
  locale: string;
  storeKey: string;
  limit: number;
}

interface MyOrdersHook {
  data: { myOrders: OrderQueryResult | undefined };
  loading: boolean;
  error?: ApolloError;
  refetch: (
    variables?: MyOrdersVars | undefined
  ) => Promise<ApolloQueryResult<MyOrdersData>>;
}
export interface MyOrdersData {
  me: { orders: OrderQueryResult };
}

const orderFragment = gql`
  fragment order on Order {
    orderNumber
    createdAt
    orderState
    paymentState
    discountCodes {
      discountCodeRef {
        typeId
        id
      }
      state
      discountCode {
        id
        code
        isActive
        name(locale: $locale)
        description(locale: $locale)
        cartDiscounts {
          id
          key
          target {
            type
          }
          value {
            ... on AbsoluteDiscountValue {
              money {
                ...money
              }
            }
            ... on FixedPriceDiscountValue {
              money {
                ...money
              }
            }
            ... on RelativeDiscountValue {
              permyriad
            }
            ... on GiftLineItemValue {
              variantId
            }
          }
        }
      }
    }
    customer {
      email
      firstName
      lastName
      addresses {
        phone
        mobile
        email
      }
    }
    totalPrice {
      ...money
    }
    taxedPrice {
      totalNet {
        ...money
      }
      totalGross {
        ...money
      }
      totalTax {
        ...money
      }
    }
    paymentInfo {
      payments {
        paymentStatus {
          interfaceCode
          interfaceText
        }
        paymentMethodInfo {
          method
          name(acceptLanguage: [$locale, "en"])
        }
      }
    }
    shippingAddress {
      ...address
    }
    billingAddress {
      ...address
    }
    shipmentState
    shippingInfo {
      deliveries {
        items {
          id
        }
        parcels {
          items {
            id
          }
          trackingData {
            carrier
            trackingId
          }
          custom {
            customFieldsRaw {
              name
              value
            }
          }
        }
      }
      shippingMethod {
        localizedName(locale: $locale)
      }
      price {
        ...money
      }
      discountedPrice {
        value {
          ...money
        }
      }
    }
    returnInfo {
      returnDate
      returnTrackingId
    }
    custom {
      customFieldsRaw {
        name
        value
      }
    }
    discountCodes {
      discountCodeRef {
        typeId
        id
      }
      state
      discountCode {
        code
        isActive
        name(locale: $locale)
        description(locale: $locale)
        cartDiscounts {
          id
          target {
            type
          }
          value {
            ... on AbsoluteDiscountValue {
              money {
                ...money
              }
            }
            ... on FixedPriceDiscountValue {
              money {
                ...money
              }
            }
            ... on RelativeDiscountValue {
              permyriad
            }
            ... on GiftLineItemValue {
              variantId
            }
          }
        }
      }
    }
    lineItems {
      id
      name(acceptLanguage: [$locale, "en"])
      productSlug(acceptLanguage: [$locale, "en"])
      quantity
      price {
        value {
          ...money
        }
      }
      state {
        state {
          name(locale: $locale)
        }
      }
      variant {
        id
        sku
        prices {
          value {
            ...money
          }
          custom {
            customFieldsRaw {
              name
              value
            }
          }
        }
        attributesRaw {
          name
          value
        }
      }
      discountedPricePerQuantity {
        quantity
        discountedPrice {
          value {
            ...money
          }
          includedDiscounts {
            discountedAmount {
              ...money
            }
            discount {
              id
              key
            }
          }
        }
      }
    }
    customLineItems {
      slug
      name(acceptLanguage: [$locale, "en"])
      totalPrice {
        ...money
      }
    }
  }

  ${moneyFragment}
  ${addressFragment}
`;

const ORDERS = gql`
  query orders($locale: Locale!, $limit: Int!) {
    me {
      orders(sort: "custom.fields.order_created_at desc", limit: $limit) {
        results {
          ...order
        }
      }
    }
  }

  ${orderFragment}
`;

type UseMyOrderProps = {
  limit?: number;
  onCompleted?: (myOrderData: MyOrdersData) => void;
  skip?: boolean;
};

const useMyOrders = ({
  limit = 50,
  onCompleted,
  skip,
}: UseMyOrderProps): MyOrdersHook => {
  const { session, accessToken } = useAuth();

  const { loading, error, data, refetch } = useQuery<
    MyOrdersData,
    MyOrdersVars
  >(ORDERS, {
    variables: {
      locale: storeConfig.locale,
      storeKey,
      limit,
    },
    context: { useCT: true, token: accessToken || "" },
    fetchPolicy: "no-cache",
    skip: !session || skip,
    onCompleted,
  });

  return { loading, error, data: { myOrders: data?.me.orders }, refetch };
};

export { useMyOrders };
